// Tiny fetch wrapper over the live Go backend (base = brand.apiBase).
// Every endpoint returns the standard envelope:
//   success -> { data: ... }
//   error   -> { error: { code, message } }
// On a non-2xx response we throw an Error whose `.code` + `.message` come from
// that envelope, so callers can surface a human-readable, inline message and
// branch on the machine code (e.g. CONFLICT, WEAK_PASSWORD).
import { brand } from "./brand.js";

export const apiRoot = brand.apiBase.replace(/\/+$/, "").replace(/\/api\/v1$/i, "");
export const apiBase = `${apiRoot}/api/v1`;
const BASE = apiRoot;

// Build a request, parse the envelope, and either return `data` or throw an
// Error carrying { code, message }. Network/parse failures get a generic but
// human-readable message and a synthetic code so nothing fails silently.
async function request(method, path, { body, token } = {}) {
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw apiError(
      "NETWORK_ERROR",
      "Could not reach the server. Check your connection and try again.",
    );
  }

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const err = payload && payload.error ? payload.error : {};
    throw apiError(
      err.code || `HTTP_${res.status}`,
      err.message || `Request failed (${res.status}). Please try again.`,
    );
  }

  return payload ? payload.data : undefined;
}

function apiError(code, message) {
  const e = new Error(message);
  e.code = code;
  return e;
}

// ---- Public (no auth) ------------------------------------------------------

export function slugAvailable(slug) {
  return request(
    "GET",
    `/api/v1/signup/slug-available?slug=${encodeURIComponent(slug)}`,
  );
}

export function signup({ fullName, email, password, companyName, slug, plan }) {
  return request("POST", "/api/v1/signup", {
    body: {
      full_name: fullName,
      email,
      password,
      company_name: companyName,
      slug,
      plan,
    },
  });
}

// POST /api/v1/signup/resend — re-send the verification email for a pending
// signup. Always 202 for a well-formed address (no enumeration).
export function resendSignup({ email }) {
  return request("POST", "/api/v1/signup/resend", { body: { email } });
}

export function verifySignup({ tenantId, token }) {
  return request("POST", "/api/v1/signup/verify", {
    body: { tenant_id: tenantId, token },
  });
}

// Not used by the signup flow, exported for completeness (teammate accept link).
export function acceptTeamInvite({ tenantId, token, fullName, password }) {
  return request("POST", "/api/v1/team/accept", {
    body: { tenant_id: tenantId, token, full_name: fullName, password },
  });
}

// ---- Native credential lifecycle (WI-P4-107) --------------------------------

// Public: ask for a reset link. Always resolves (202) whether or not the
// account exists, so nothing here can be used to enumerate accounts.
export function forgotPassword(email) {
  return request("POST", "/api/v1/password/forgot", { body: { email } });
}

// Public: redeem the emailed link (tenant_id + token) with a new password.
export function resetPassword({ tenantId, token, password }) {
  return request("POST", "/api/v1/password/reset", {
    body: { tenant_id: tenantId, token, password },
  });
}

// Authed (native session only): rotate your own password.
export function changePassword(token, currentPassword, newPassword) {
  return request("POST", "/api/v1/account/password", {
    token,
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

// Admin/owner: send a teammate a reset link. Without a mailer the response
// carries `reset_url` exactly once (same manual-delivery rule as invites).
export function sendMemberPasswordReset(token, userId) {
  return request(
    "POST",
    `/api/v1/admin/team/members/${encodeURIComponent(userId)}/password-reset`,
    { token },
  );
}

// ---- Owner auth ------------------------------------------------------------

// Owner login: exchange email + password for a <SESSION> whose `.token` is the
// bearer used by the authed admin calls below. (Public endpoint — no token.)
export function ownerLogin(email, password) {
  return request("POST", "/api/v1/owner/login", {
    body: { email, password },
  });
}

// ---- Authed (owner bearer token from a verified <SESSION>) -----------------

// Read the full admin widget-config row. `data` may be `null` if never set.
export function getAdminWidgetConfig(token, applicationId = "", environmentId = "") {
  const params = new URLSearchParams();
  if (applicationId && environmentId) {
    params.set("application_id", applicationId);
    params.set("environment_id", environmentId);
  }
  const query = params.toString();
  return request("GET", `/api/v1/admin/widget-config${query ? `?${query}` : ""}`, { token });
}

// Persist the widget-config row. `body` is the full config object.
export function putWidgetConfig(token, body) {
  return request("PUT", "/api/v1/admin/widget-config", { token, body });
}

export function createInvite({ token, email, orgRole }) {
  return request("POST", "/api/v1/admin/team/invites", {
    token,
    body: { email, org_role: orgRole },
  });
}

// `scopes` is optional; without it the server picks the key type's defaults.
export function createKey({ token, name, keyType, applicationId = "", environmentId = "", scopes }) {
  return request("POST", "/api/v1/admin/keys", {
    token,
    body: {
      name,
      key_type: keyType,
      application_id: applicationId,
      environment_id: environmentId,
      ...(Array.isArray(scopes) && scopes.length ? { scopes } : {}),
    },
  });
}

export function listApplications(token) {
  return request("GET", "/api/v1/admin/applications", { token });
}

export function createApplication({ token, name, slug, environmentName = "Production", environmentSlug = "production", allowedOrigins = [] }) {
  return request("POST", "/api/v1/admin/applications", {
    token,
    body: { name, slug, default_environment: { name: environmentName, slug: environmentSlug, allowed_origins: allowedOrigins, is_published: false } },
  });
}

export function listApplicationAccess(token) {
  return request("GET", "/api/v1/admin/application-access", { token });
}

export function setApplicationAccess(token, userId, mode, applicationIds) {
  return request("PUT", `/api/v1/admin/application-access/${encodeURIComponent(userId)}`, {
    token,
    body: { mode, application_ids: applicationIds },
  });
}

// ---- Admin console reads/writes (owner/admin bearer) ------------------------

// Workspace identity card: { tenant_id, name, slug, plan, status, created_at }.
export function getWorkspace(token) {
  return request("GET", "/api/v1/admin/workspace", { token });
}

// POST /api/v1/admin/import/drift (WI-P4-120): parsed Drift CSV rows in,
// per-row outcomes out. `dryRun` reports what would change without writing.
export function importDrift(token, { dryRun, defaultOrgRole, teammates = [], contacts = [] }) {
  const body = { dry_run: !!dryRun };
  if (defaultOrgRole) body.default_org_role = defaultOrgRole;
  if (teammates.length) body.teammates = teammates;
  if (contacts.length) body.contacts = contacts;
  return request("POST", "/api/v1/admin/import/drift", { token, body });
}

export function listMembers(token) {
  return request("GET", "/api/v1/admin/team/members", { token });
}

export function listInvites(token) {
  return request("GET", "/api/v1/admin/team/invites", { token });
}

export function revokeInvite(token, invitationId) {
  return request(
    "DELETE",
    `/api/v1/admin/team/invites/${encodeURIComponent(invitationId)}`,
    { token },
  );
}

export function changeMemberRole(token, userId, orgRole) {
  return request(
    "PATCH",
    `/api/v1/admin/team/members/${encodeURIComponent(userId)}`,
    { token, body: { org_role: orgRole } },
  );
}

export function removeMember(token, userId) {
  return request(
    "DELETE",
    `/api/v1/admin/team/members/${encodeURIComponent(userId)}`,
    { token },
  );
}

// Safe key metadata only (prefix, scopes, timestamps) — never the secret.
export function listKeys(token) {
  return request("GET", "/api/v1/admin/keys", { token });
}

// Returns the NEW secret exactly once.
export function rotateKey(token, keyId) {
  return request(
    "POST",
    `/api/v1/admin/keys/${encodeURIComponent(keyId)}/rotate`,
    { token },
  );
}

export function revokeKey(token, keyId) {
  return request("DELETE", `/api/v1/admin/keys/${encodeURIComponent(keyId)}`, {
    token,
  });
}

// Rolled-up daily metrics: [{ day, metric, value }].
export function listUsage(token) {
  return request("GET", "/api/v1/admin/usage", { token });
}

// Month-to-date totals + straight-line projections + plan limits:
// { month, days_elapsed, days_in_month, plan, metrics: [...], operators: {...} }.
export function getUsageSummary(token) {
  return request("GET", "/api/v1/admin/usage/summary", { token });
}

// Retention policy: { notification_retention_days, audit_log_retention_days, ... }.
// Iris Assist policy (WI-P4-106). The routes exist only when the server has
// UNICHAT_ASSIST_ENABLED=true; a 404 means "assist is off on this environment".
export function getAssistPolicy(token) {
  return request("GET", "/api/v1/admin/assist-policy", { token });
}

export function putAssistPolicy(token, body) {
  return request("PUT", "/api/v1/admin/assist-policy", { token, body });
}

// Workspace AI provider keys (WI-P4-108), owners/admins only. A workspace can
// save several providers (one per provider and endpoint) and mark one the
// default. Keys are write-only: responses carry provider, model, endpoint, the
// key's last four characters, consent, and who saved it — never a key.
const AI_PROVIDERS = "/api/v1/admin/integrations/ai-providers";

// { credentials: [...], effective, platform, providers, storage_ready, max_credentials }.
export function listAIProviders(token) {
  return request("GET", AI_PROVIDERS, { token });
}

// Adds one provider. cloudConsent is the add form's own tick for this provider.
export function addAIProvider(token, { provider, model, baseUrl, apiKey, label, cloudConsent, makeDefault }) {
  return request("POST", AI_PROVIDERS, {
    token,
    body: {
      provider,
      model: model || "",
      base_url: baseUrl || "",
      api_key: apiKey || "",
      label: label || "",
      cloud_consent: !!cloudConsent,
      make_default: !!makeDefault,
    },
  });
}

// Changes one saved provider's model, name, key, or consent. The provider and
// endpoint never change; an empty apiKey keeps the saved key; cloudConsent is
// sent only when given (undefined keeps it). expectedUpdatedAt is the
// updated_at the form opened with: if someone changed the provider since, the
// server answers 409 CHANGED instead of undoing their change.
export function updateAIProvider(token, id, { model, label, apiKey, cloudConsent, expectedUpdatedAt }) {
  const body = { model: model || "", label: label || "" };
  if (apiKey) body.api_key = apiKey;
  if (cloudConsent !== undefined) body.cloud_consent = !!cloudConsent;
  if (expectedUpdatedAt) body.expected_updated_at = expectedUpdatedAt;
  return request("PUT", `${AI_PROVIDERS}/${encodeURIComponent(id)}`, { token, body });
}

export function removeAIProvider(token, id) {
  return request("DELETE", `${AI_PROVIDERS}/${encodeURIComponent(id)}`, { token });
}

export function setDefaultAIProvider(token, id) {
  return request("POST", `${AI_PROVIDERS}/${encodeURIComponent(id)}/default`, { token });
}

// One minimal live call with a saved key: { ok, error_class?, provider, model }.
export function testAIProvider(token, id) {
  return request("POST", `${AI_PROVIDERS}/${encodeURIComponent(id)}/test`, { token });
}

// The models a provider offers for a key typed in the form (never stored):
// { ok, error_class?, models: [{ id, name, created_at?, context_window?, max_output? }], source }.
export function listProviderModels(token, { provider, baseUrl, apiKey }) {
  return request("POST", `${AI_PROVIDERS}/models`, {
    token,
    body: { provider, base_url: baseUrl || "", api_key: apiKey || "" },
  });
}

// The models for a saved provider, with its own key at its own endpoint.
export function listSavedProviderModels(token, id) {
  return request("GET", `${AI_PROVIDERS}/${encodeURIComponent(id)}/models`, { token });
}

// ---- Integrations: signed outbound webhooks (WI-P4-117), owners/admins only --

// { endpoints: [EndpointView], event_types: [...], storage_ready }.
export function listWebhooks(token) {
  return request("GET", "/api/v1/admin/webhooks", { token });
}

// New chat alerts + visitor follow-up (WI-P4-120 slice 2).
export function getNotificationSettings(token) {
  return request("GET", "/api/v1/admin/notifications", { token });
}

export function putNotificationSettings(token, body) {
  return request("PUT", "/api/v1/admin/notifications", { token, body });
}

// Returns { endpoint, signing_secret }; the secret is shown exactly once.
// Omitting eventTypes subscribes the endpoint to every event type.
// `signingSecret` (optional) is the secret the app already holds; omitted, the
// server generates one and returns it once.
export function createWebhook(token, { name, url, eventTypes, signingSecret }) {
  return request("POST", "/api/v1/admin/webhooks", {
    token,
    body: {
      name,
      url,
      ...(Array.isArray(eventTypes) ? { event_types: eventTypes } : {}),
      ...(signingSecret ? { signing_secret: signingSecret } : {}),
    },
  });
}

// `patch` is any of { name, url, event_types, is_active } (is_active false = paused).
export function updateWebhook(token, webhookId, patch) {
  return request("PATCH", `/api/v1/admin/webhooks/${encodeURIComponent(webhookId)}`, {
    token,
    body: patch,
  });
}

export function deleteWebhook(token, webhookId) {
  return request("DELETE", `/api/v1/admin/webhooks/${encodeURIComponent(webhookId)}`, { token });
}

// Returns { endpoint, signing_secret }; the old secret keeps signing for 24 h.
export function rotateWebhookSecret(token, webhookId) {
  return request("POST", `/api/v1/admin/webhooks/${encodeURIComponent(webhookId)}/rotate-secret`, { token });
}

// Sends a signed iris.ping: { delivered, status_code?, receiver_status?, receiver_reason?, error?, duration_ms }.
export function testWebhook(token, webhookId) {
  return request("POST", `/api/v1/admin/webhooks/${encodeURIComponent(webhookId)}/test`, { token });
}

// Newest first, at most 25.
export function listWebhookDeliveries(token, webhookId) {
  return request("GET", `/api/v1/admin/webhooks/${encodeURIComponent(webhookId)}/deliveries`, { token });
}

export function retryWebhookDelivery(token, webhookId, deliveryId) {
  return request(
    "POST",
    `/api/v1/admin/webhooks/${encodeURIComponent(webhookId)}/deliveries/${encodeURIComponent(deliveryId)}/retry`,
    { token },
  );
}

export function getRetentionPolicy(token) {
  return request("GET", "/api/v1/admin/retention-policy", { token });
}

// Billing state: { configured, purchasable_plans, subscription? }.
export function getBillingState(token) {
  return request("GET", "/api/v1/admin/billing", { token });
}

// Starts a Stripe checkout; returns { url } to redirect the browser to.
export function createBillingCheckout(token, planId) {
  return request("POST", "/api/v1/admin/billing/checkout", { token, body: { plan_id: planId } });
}

// Opens the Stripe billing portal; returns { url }.
export function createBillingPortal(token) {
  return request("POST", "/api/v1/admin/billing/portal", { token });
}
